/**
 * <product-personalize-fields>: gedrag van snippets/product-personalize-fields.liquid.
 *
 * - Voorwaardelijke velden (`data-if-id` + `data-if-in` / `data-if-not-in`): zichtbaar zolang de
 *   waarde van het verwijzende veld klopt. Een verborgen veld wordt ook uitgeschakeld (disabled),
 *   dus niet meegestuurd met het formulier en niet gecontroleerd.
 * - Voorwaarden op de gekozen variant: `option1` / `option2` / `option3` in `data-conditions` (zie de
 *   doc van snippets/product-personalize-fields.liquid). De huidige variant komt uit het verborgen
 *   `id`-veld van het productformulier; het volgnummer van elke optiewaarde uit `data-role="variant-options"`.
 * - Datumvelden: het zichtbare veld levert ISO; naar de winkelwagen gaat een uitgeschreven datum
 *   in een verborgen veld ("3 december 2026", met tijd bij datetime). Bewust vast Nederlands,
 *   want de lezer is wijzelf (zelfde keuze als assets/product-personalize.js).
 * - Verplichte velden: het productformulier heeft `novalidate`, dus `required` houdt niemand
 *   tegen. Net als product-personalize.js luistert dit element naar `product-form:collect-lines`
 *   (assets/product-form-lines.js) en roept `detail.cancel()` aan zodra een zichtbaar verplicht
 *   veld leeg is.
 */
class ProductPersonalizeFields extends HTMLElement {
  connectedCallback() {
    if (this.ready) return;
    this.ready = true;

    this.status = this.querySelector('[data-role="status"]');
    const i18nEl = this.querySelector('[data-role="i18n"]');
    this.i18n = i18nEl ? JSON.parse(i18nEl.textContent) : {};
    this.fields = [...this.querySelectorAll('[data-field]')];
    const optionsEl = this.querySelector('[data-role="variant-options"]');
    this.variantOptions = optionsEl ? JSON.parse(optionsEl.textContent) : {};

    this.addEventListener('input', (event) => this.onChange(event));
    this.addEventListener('change', (event) => this.onChange(event));
    this.querySelectorAll('[data-date-output]').forEach((input) => this.syncDate(input));
    this.applyConditions();
    document.addEventListener('click', (event) => this.onExpressClick(event), true);

    document.addEventListener('product-form:collect-lines', (event) => this.collectLines(event.detail));
    // Varianten wisselen via een `change` op het verborgen id-veld van het productformulier (assets/product-info.js).
    document.addEventListener('change', (event) => {
      if (event.target.name === 'id' && this.form && this.form.contains(event.target)) this.applyConditions();
    });
  }

  get form() {
    return this.dataset.form ? document.getElementById(this.dataset.form) : null;
  }

  // Volgnummer (0 = eerste waarde) van de gekozen waarde bij optie n van de huidige variant; '' als onbekend.
  optionIndex(n) {
    const id = this.form && this.form.querySelector('input[name="id"]');
    const idx = id && this.variantOptions[id.value];
    return idx && idx[n - 1] !== undefined ? String(idx[n - 1]) : '';
  }

  get pageSection() {
    return this.closest('[id^="shopify-section"]') || document;
  }

  onChange(event) {
    if (event.target.dataset.dateOutput) this.syncDate(event.target);
    this.applyConditions();
  }

  // Huidige waarde van een veld (per id); leeg als er niets gekozen/ingevuld is of het veld verborgen is.
  valueOf(id) {
    const option = /^option([1-3])$/.exec(id);
    if (option) return this.optionIndex(Number(option[1]));
    const wrap = this.fields.find((el) => el.dataset.field === id);
    if (!wrap || wrap.hidden) return '';
    const inputs = [...wrap.querySelectorAll('[data-role="input"]')];
    const radio = inputs.filter((el) => el.type === 'radio');
    if (radio.length) return (radio.find((el) => el.checked) || {}).value || '';
    const input = inputs[0];
    if (!input) return '';
    if (input.type === 'checkbox') return input.checked ? input.value : '';
    return input.value || '';
  }

  parseList(raw) {
    try {
      const list = JSON.parse(raw || 'null');
      return Array.isArray(list) ? list : null;
    } catch (e) {
      return null;
    }
  }

  // `sleutel=a|b` of `sleutel!=a|b`, één per regel; alle regels moeten kloppen.
  parseConditions(raw) {
    return (raw || '')
      .split(/\r?\n/)
      .map((line) => /^\s*([\w-]+)\s*(!=|=)\s*(.*?)\s*$/.exec(line))
      .filter(Boolean)
      .map((m) => ({ key: m[1], negate: m[2] === '!=', values: m[3].split('|') }));
  }

  shouldShow(wrap) {
    let show = true;
    if (wrap.dataset.ifId) {
      const current = this.valueOf(wrap.dataset.ifId);
      const allowed = this.parseList(wrap.dataset.ifIn);
      const denied = this.parseList(wrap.dataset.ifNotIn);
      if (allowed) show = allowed.includes(current);
      if (denied && denied.includes(current)) show = false;
    }
    return this.parseConditions(wrap.dataset.conditions).reduce((ok, cond) => {
      const hit = cond.values.includes(this.valueOf(cond.key));
      return ok && (cond.negate ? !hit : hit);
    }, show);
  }

  // Meerdere keren herberekenen: een voorwaarde kan op een veld wijzen dat zelf weer voorwaardelijk is.
  applyConditions() {
    for (let pass = 0; pass < this.fields.length; pass++) {
      let changed = false;
      this.fields.forEach((wrap) => {
        if (!wrap.dataset.ifId && !wrap.dataset.conditions) return;
        const show = this.shouldShow(wrap);
        if (wrap.hidden === !show) return;
        wrap.hidden = !show;
        wrap.querySelectorAll('input, select, textarea').forEach((el) => {
          el.disabled = !show;
        });
        changed = true;
      });
      if (!changed) break;
    }
    this.syncExpressCheckout();
    // De knoppen worden na een variantwissel opnieuw opgebouwd; daarna nogmaals bijwerken.
    clearTimeout(this.expressTimer);
    this.expressTimer = setTimeout(() => this.syncExpressCheckout(), 600);
  }

  syncDate(input) {
    const out = this.querySelector(`#${CSS.escape(input.dataset.dateOutput)}`);
    if (!out) return;
    const match = /^(\d{4})-(\d{2})-(\d{2})(?:T(\d{2}):(\d{2}))?$/.exec(input.value);
    if (!match) {
      out.value = '';
      return;
    }
    const date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
    let text = date.toLocaleDateString('nl-NL', { day: 'numeric', month: 'long', year: 'numeric' });
    if (match[4]) text += ` ${match[4]}:${match[5]}`;
    out.value = text;
  }

  isEmpty(wrap) {
    const input = wrap.querySelector('[data-role="input"]');
    if (!input) return false;
    return this.valueOf(wrap.dataset.field).trim() === '';
  }

  collectLines(detail) {
    if (!this.pageSection.contains(detail.form)) return;

    this.querySelectorAll('.is-invalid').forEach((el) => el.classList.remove('is-invalid'));
    if (this.status) this.status.hidden = true;
    this.querySelectorAll('[data-date-output]').forEach((input) => this.syncDate(input));

    const bad = this.findInvalid();
    if (!bad) return;

    detail.cancel();
    this.reportInvalid(bad);
  }

  // Eerste zichtbare verplichte veld dat nog leeg is.
  findInvalid() {
    return this.fields.find((wrap) => {
      if (wrap.hidden) return false;
      const input = wrap.querySelector('[data-role="input"]');
      return input && wrap.hasAttribute('data-required') && this.isEmpty(wrap);
    });
  }

  reportInvalid(bad) {
    const target = bad.querySelector('[data-role="input"]');
    bad.querySelectorAll('.field__input, .select__select, .pp__radio-group').forEach((el) => el.classList.add('is-invalid'));
    if (this.status) {
      this.status.hidden = false;
      this.status.setAttribute('role', 'alert');
      this.status.textContent = (this.i18n.required || '').replace('__FIELD__', bad.dataset.label || '');
    }
    target.focus({ preventScroll: true });
    bad.scrollIntoView({ block: 'center', behavior: 'smooth' });
  }

  // Dynamische checkout-knoppen (Apple Pay, PayPal, ...) sturen het formulier niet via een submit-
  // gebeurtenis en staan deels in een iframe, dus de controle hierboven bereikt ze niet. Zolang er
  // een verplicht veld leeg is leggen we er daarom een onzichtbare klikvanger overheen
  // (.pp-blocked, zie component-product-personalize.css); een klik daarop toont dezelfde melding.
  syncExpressCheckout() {
    const buttons = this.form && this.form.querySelectorAll('.shopify-payment-button');
    if (!buttons) return;
    const blocked = !!this.findInvalid();
    buttons.forEach((el) => el.classList.toggle('pp-blocked', blocked));
  }

  onExpressClick(event) {
    const button = event.target.closest && event.target.closest('.shopify-payment-button.pp-blocked');
    if (!button || !this.form || !this.form.contains(button)) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    const bad = this.findInvalid();
    if (bad) this.reportInvalid(bad);
  }
}

if (!customElements.get('product-personalize-fields')) {
  customElements.define('product-personalize-fields', ProductPersonalizeFields);
}
